import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';

import { useTRPC } from '#src/trpc.js';
import { useAutoSave } from '#utils/use-auto-save/use-auto-save.js';
import { PackageTable } from './package-table.js';
import { getPackageTableRows, packageTableRowToPkg } from './utils.js';

import type { HilPackage } from '@cadstrom/backend/shared';

export const Route = createFileRoute('/_authorization-gate/package-store/$packageFamilyId/packages/')({
	staticData: {
		navigation: {
			label: 'Family: Packages',
			keywords: ['list'],
		},
	},
	loader: async ({ context: { queryClient, getPackageFamilyOptions, getFamilyPackagesOptions } }) => {
		void queryClient.ensureQueryData(getFamilyPackagesOptions);
		const { pkgFamily } = await queryClient.ensureQueryData(getPackageFamilyOptions);
		return { pkgFamily };
	},
	head: ({ loaderData }) => ({
		meta: [
			{ title: (loaderData ? `Family ${loaderData.pkgFamily.name}: ` : '') + 'Packages • Package Editor • Cadstrom' },
		],
	}),
	component: PackagesTab,
});

function PackagesTab() {
	const queryClient = useQueryClient();
	const trpc = useTRPC();

	const { getFamilyPackagesOptions } = Route.useRouteContext();
	const { pkgFamily } = Route.useLoaderData();

	const {
		data: { pkgs },
	} = useSuspenseQuery(getFamilyPackagesOptions);

	const { mutateAsync: createPackages, isPending: createPackagesIsPending } = useMutation(
		trpc.package.createMany.mutationOptions({
			onSuccess: (res) => {
				queryClient.setQueryData(getFamilyPackagesOptions.queryKey, (data) => ({
					pkgs: [...(data?.pkgs ?? []), ...res.pkgs],
				}));

				setServerPackages((prev) => [...prev, ...getPackageTableRows(res.pkgs)]);
				setDraftPackages((prev) =>
					prev.map((pkg) => (res.pkgs.find((p) => p.id === pkg.id) ? { ...pkg, isDraft: false } : pkg)),
				);
			},
		}),
	);

	const { mutateAsync: updatePackages, isPending: updatePackagesIsPending } = useMutation(
		trpc.package.updateMany.mutationOptions({
			onSuccess: (res) => {
				queryClient.setQueryData(getFamilyPackagesOptions.queryKey, (data) => ({
					pkgs: (data?.pkgs ?? []).map((pkg) => res.pkgs.find((p) => p.id === pkg.id) ?? pkg),
				}));

				setServerPackages((prev) =>
					prev.map((pkg) => {
						const updatedPkg = res.pkgs.find((p) => p.id === pkg.id);
						return updatedPkg ? { ...updatedPkg, isDraft: false } : pkg;
					}),
				);
			},
		}),
	);

	const { mutateAsync: deletePackages, isPending: deletePackagesIsPending } = useMutation(
		trpc.package.deleteMany.mutationOptions({
			onSuccess: (res) => {
				queryClient.setQueryData(getFamilyPackagesOptions.queryKey, (data) => ({
					pkgs: (data?.pkgs ?? []).filter((p) => !res.ids.includes(p.id)),
				}));

				setServerPackages((prev) => prev.filter((p) => !res.ids.includes(p.id)));
			},
		}),
	);

	const isSaving = createPackagesIsPending || updatePackagesIsPending || deletePackagesIsPending;

	const {
		draftState: draftPackages,
		setDraftState: setDraftPackages,
		setServerState: setServerPackages,
		isDirty,
		handleSave,
		saveAreaContainerRef,
	} = useAutoSave({
		initialState: getPackageTableRows(pkgs),
		mutationKeys: [
			trpc.package.createMany.mutationKey(),
			trpc.package.updateMany.mutationKey(),
			trpc.package.deleteMany.mutationKey(),
		],
		saveFn: async (draftPackages, serverPackages) => {
			const packagesToCreate: HilPackage[] = [];
			const packagesToUpdate: HilPackage[] = [];
			const packagesToDelete: string[] = [];

			for (const draftPackage of draftPackages) {
				if (!serverPackages.find((p) => p.id === draftPackage.id)) {
					packagesToCreate.push(packageTableRowToPkg(draftPackage));
				} else if (
					draftPackage.manufacturerPartNumber !==
					serverPackages.find((p) => p.id === draftPackage.id)?.manufacturerPartNumber
				) {
					packagesToUpdate.push(packageTableRowToPkg(draftPackage));
				}
			}

			for (const serverPackage of serverPackages) {
				if (!draftPackages.find((p) => p.id === serverPackage.id)) {
					packagesToDelete.push(serverPackage.id);
				}
			}

			if (packagesToCreate.length > 0) {
				await createPackages({
					data: packagesToCreate,
				});
			}

			if (packagesToUpdate.length > 0) {
				await updatePackages({
					pkgs: packagesToUpdate.map((pkg) => ({
						id: pkg.id,
						data: {
							manufacturerPartNumber: pkg.manufacturerPartNumber,
						},
					})),
				});
			}

			if (packagesToDelete.length > 0) {
				await deletePackages({
					ids: packagesToDelete,
				});
			}
		},
		equalityFn: (serverData, draftData) => {
			const hasNewOrModifiedPackages = draftData.some((draftPkg) => {
				const matchingServerPkg = serverData.find((serverPkg) => serverPkg.id === draftPkg.id);
				return (
					// Check if package is new (not in server data)
					!matchingServerPkg || // Check if manufacturer part number has changed
					draftPkg.manufacturerPartNumber !== matchingServerPkg.manufacturerPartNumber
				);
			});
			// Check if any server packages were deleted
			const hasDeletedPackages = serverData.some(
				(serverPkg) => !draftData.find((draftPkg) => draftPkg.id === serverPkg.id),
			);
			return !hasNewOrModifiedPackages && !hasDeletedPackages;
		},
	});

	return (
		<div ref={saveAreaContainerRef} className='flex min-h-0 grow basis-0 flex-col px-3 py-2'>
			<PackageTable
				pkgFamily={pkgFamily}
				rows={draftPackages}
				setPkgs={setDraftPackages}
				onClickSave={handleSave}
				isSaving={isSaving}
				isDirty={isDirty}
			/>
		</div>
	);
}
